/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, MouseEvent, useEffect, useMemo, useState} from 'react';
import {IconButton} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {isDeviceAlert} from '@ion/core/src/services/AlertUtils';
import {RoundedChipButton} from '@ion/dls/src/components/buttons/RoundedChipButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {CardLabel} from '@ion/dls/src/components/labels/CardLabel';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {TimeStamp} from '@ion/dls/src/components/time/TimeStamp';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {StatusIcon} from '@ion/dls/src/icons/StatusIcon';
import {TrashCanIcon} from '@ion/dls/src/icons/TrashCanIcon';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import NotificationDetails from './NotificationDetails';

export interface NotificationCardType {
  id?: string | number;
  toastId?: string | number;
  notification?: Notification;
  read?: boolean
  text: string;
  subText?: string;
  type?: string;
  timeStamp?: string;
  viewEventCta?: JSX.Element | null;
  onReviewClick?: (notification: Notification) => void;
  onCardClick?: (notification: Notification, isChecked?: boolean) => void;
  onCloseClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onClearClick?: (notification: Notification) => void;
  onCheckBoxChange?: (checked: boolean, notification: Notification) => void;
  onHover?: (notification: Notification) => void;
  onBlur?: (notification: Notification) => void;
  isToast?: boolean;
  shadowOnHover?: boolean;
  isUrgent?: boolean;
  multiSelect?: boolean;
  onMoreClick?: (notification: Notification) => void;
  onLessClick?: (notification: Notification) => void;
  onDoubleClick?: (id: string | number) => void;
  moreInfoLoading?: boolean;
  moreInfoDetails?: JSX.Element | null;
  showMoreInfo?: boolean;
  showCheckBox?: boolean;
  isChecked?: boolean;
  disableSingleClear?: boolean;
  isCheckAll?: boolean;
  rootStyles?: {
    [key: string]: string;
  };
  onToastRemoved?: (id: string | number) => void;
  isHideViewEventCta?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          smallExpansion: {
            open: expansionOpen
          }
        }
      }
    }
  } = theme;
  return {
    notificationCard : {
      minHeight                      : '136px',
      maxWidth                       : '414px',
      padding                        : '18px',
      backgroundColor                : dlsColor('white'),
      borderBottom                   : ({isToast}: {isToast: boolean}) => isToast ? 'none' : `solid 1px ${dlsColor('grey5')}`,
      [theme.breakpoints.down('sm')] : {
        maxWidth : 'none'
      }
    },
    notificationCardSelected : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    },
    hoverEffect : {
      '&:hover' : {
        backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
      }
    },
    notificationCardHeader : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'center',
      marginBottom   : '12px'
    },
    notificationCardSubHeader : {
      margin : '-15px 0 11px 4px'
    },
    notificationCardHeaderRight : {
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      minHeight      : '38px'
    },
    notificationTimeStamp : {
      paddingLeft : '4px',
      marginRight : ({read}: {read: boolean}) => read ? '-12px' : 0,
      ...theme.dls?.typography.caption
    },
    notificationCardTextWrapper : {
      margin : '4px 0'
    },
    notificationCardText : {
      minHeight    : '48px',
      overflow     : 'hidden',
      textOverflow : 'ellipsis',
      ...theme.dls?.typography.body1
    },
    notificationReadIcon : {
      opacity    : '0',
      transition : 'opacity 300ms ease-out'
    },
    notificationUnreadIcon : {
      opacity     : '1',
      display     : 'flex',
      alignItems  : 'center',
      marginRight : '5px'
    },
    moreInfoButton : {
      border          : 'none',
      backgroundColor : 'transparent',
      outline         : 'none',
      padding         : '0',
      margin          : '5px 0',
      marginTop       : ({isValidEntityCTA}: {isValidEntityCTA: boolean}) => isValidEntityCTA ? '12px' : '-18px',
      textDecoration  : 'underline',
      position        : 'absolute',
      right           : '18px',
      minWidth        : '90px',
      display         : 'flex',
      justifyContent  : 'center',
      alignItems      : 'center',
      ...theme.dls?.typography.body1,
      '&:hover'       : {
        cursor : 'pointer'
      }
    },
    notificationMoreInfo : {
      overflow   : 'hidden',
      transition : `max-height ${expansionOpen}`,
      margin     : '15px 0'
    },
    notificationHoverCta : {
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      minWidth       : '50px',
      minHeight      : '30px'
    },
    trashIconHover : {
      display        : 'flex',
      justifyContent : 'flex-end',
      alignItems     : 'center',
      minHeight      : '30px'
    },
    moreInfoWrapper : {
      margin     : '15px 0',
      transition : 'max-height 300ms ease-in',
      overflowY  : 'auto',
      overflowX  : 'clip'
    },
    moreInfoWrapperHidden : {
      maxHeight  : '0px',
      margin     : '15px 0',
      overflow   : 'hidden',
      transition : 'max-height 300ms ease-out'
    },
    notificationCheckboxWrapper : {
      maxWidth : '25px'
    },
    notificationCancelClear : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center',
      marginLeft     : '3px',
      cursor         : 'pointer'
    },
    noLeftPadding : {
      paddingLeft : '0 !important'
    },
    ctaContainer : {
      marginLeft : '-4px',
      display    : 'flex',
      alignItems : 'center'
    },
    viewAssetCta : {
      padding  : '4px 4px',
      fontSize : '14px'
    }
  };
});

/**
 * @function NotificationCard
 * @returns Node
 *
 * Card that can be used for notifications. isToast boolean prop
 * will hide some info and just show a close button that fires a
 * callback.
 *
 * ```typescript
 * import {NotificationCard} from '@ion/dls/src/notifications'
 *
 * <NotificationCard read
 *   isToast
 *   isUrgent
 *   notification={{...}}
 *   text='Notification Title'
 *   type='Time Related'
 *   timeStamp='Mon Nov 02 2020 21:58:43'
 *   onDebouncedHover={handleHover} />
 *
 * ```
 *
 */
export const NotificationCard = ({
  id,
  text,
  subText,
  type,
  timeStamp,
  viewEventCta,
  onReviewClick,
  onCardClick,
  onCloseClick,
  onClearClick,
  onCheckBoxChange,
  onHover,
  onBlur,
  notification,
  isUrgent,
  onMoreClick,
  onLessClick,
  onDoubleClick,
  moreInfoLoading = false,
  showMoreInfo = false,
  isChecked = false,
  showCheckBox = false,
  isCheckAll = false,
  rootStyles = {},
  read = false,
  isToast = false,
  isHideViewEventCta = false,
  disableSingleClear = false
}: NotificationCardType) => {
  const [showTrashCan, setShowTrashCan]       = useState(false);
  const [showClearButton, setShowClearButton] = useState(false);
  const {isDesktop, isPhone}                  = useMedia();
  const {assetId, device}                     = notification || {};

  const isValidEntityCTA = useMemo(() => isDeviceAlert(notification) ? !!device : !!assetId, [notification]);
  const classes          = useStyles({isToast, isValidEntityCTA, read});

  function handleCardClick(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    onCardClick?.(notification, isChecked);
  }

  function handleReviewClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onReviewClick?.(notification);
  }

  function handleCloseClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onCloseClick?.(e);
  }

  function handleMoreClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onMoreClick?.(notification);
  }

  function handleLessClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onLessClick?.(notification);
  }

  function handleMouseEnter(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    if (!isToast) {
      if (!isPhone) {
        setShowTrashCan(true);
      }
      onHover?.(notification);
    }
  }

  function handleDoubleClick(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    onDoubleClick?.(id);
  }

  function handleMouseLeave() {
    if (!isToast) {
      if (!isPhone) {
        setShowTrashCan(false);
      }
      onBlur?.(notification);
    }
  }

  function handleTrashHover(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    setShowClearButton(true);
  }

  function handleTrashBlur() {
    setShowClearButton(false);
  }

  function handleClearClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onClearClick?.(notification);
    setShowClearButton(false);
  }

  function handleMobileTrashClick(e: MouseEvent<HTMLSpanElement>) {
    e.stopPropagation();
    setShowClearButton(true);
  }

  function handleCheckBoxClick(e: ChangeEvent<HTMLInputElement>) {
    e.stopPropagation();
    onCheckBoxChange?.(e.target.checked, notification);
    onCardClick?.(notification, isChecked);
  }

  function handleMobileClearCancel(e: MouseEvent<HTMLSpanElement>) {
    e.stopPropagation();
    setShowClearButton(false);
  }

  return (
    <div className={classnames(classes.notificationCard, {
      [classes.notificationCardSelected] : isChecked,
      [classes.hoverEffect]              : isDesktop
    })}
    onClick={handleCardClick}
    onDoubleClick={handleDoubleClick}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
    {...rootStyles}>
      <div className={classes.notificationCardHeader}>
        <CardLabel textColor={isUrgent ? dlsColor('white') : dlsColor('grey1')}
          color={isUrgent ? dlsColor('caRed') : dlsColor('grey6')}
          labelText={type} />
        <div className={classes.notificationCardHeaderRight}>
          <span className={classes.notificationTimeStamp}>
            <TimeStamp time={timeStamp} daysAgo />
          </span>
          <span className={
            read ? classes.notificationReadIcon : classes.notificationUnreadIcon
          }>
            <StatusIcon type={dlsColor('caBlue')} />
          </span>
          {
            isToast ? (
              <IconButton size='small'
                onClick={handleCloseClick}>
                <CloseIcon fontSize='small' />
              </IconButton>
            ) : (
              <>
                {
                  isChecked ? (
                    <div className={classes.notificationCheckboxWrapper}>
                      <CheckBox onChange={handleCheckBoxClick}
                        checked={isChecked} />
                    </div>
                  ) : (
                    <>
                      {
                        !disableSingleClear && showTrashCan && (
                          <div onMouseEnter={handleTrashHover} onMouseLeave={handleTrashBlur}
                            className={showClearButton ? (classes.notificationHoverCta)
                              : (classes.trashIconHover)}>
                            <Hidden hide='mobile'>
                              {
                                showClearButton
                                  ? <RoundedChipButton onClick={handleClearClick}
                                    labelText={translate('notifications:panel.clear')} />
                                  : <TrashCanIcon />
                              }
                            </Hidden>
                          </div>
                        )
                      }
                    </>
                  )
                }
                <Hidden hide='desktop'>
                  {
                    !disableSingleClear && !showClearButton
                      ? <div onClick={handleMobileTrashClick}><TrashCanIcon /></div>
                      : showClearButton ? (
                        <>
                          <RoundedChipButton onClick={handleClearClick}
                            labelText='Clear' />
                          <span className={classes.notificationCancelClear}
                            onClick={handleMobileClearCancel}>
                            <MaterialExitCloseIcon size={IconSize.small} />
                          </span>
                        </>
                      ) : null
                  }
                </Hidden>
              </>
            )
          }
        </div>
      </div>
      <div className={classes.notificationCardText}>
        <HeaderTypography variant='h4'>{text}</HeaderTypography>
        {subText && <BodyTypography className={classes.noLeftPadding}>{subText}</BodyTypography>}
        {
          !isToast && (
            <div className={
              showMoreInfo && !moreInfoLoading
                ? classes.moreInfoWrapper
                : classes.moreInfoWrapperHidden
            }>
              {!moreInfoLoading && notification?.details &&
              <NotificationDetails notification={notification} />}
            </div>
          )
        }
      </div>
      {
        !isToast && (
          <>
            <button onClick={showMoreInfo ? handleLessClick : handleMoreClick}
              disabled={moreInfoLoading}
              className={classes.moreInfoButton}>
              {
                moreInfoLoading
                  ? <IonSpinner width='20px' height='20px' />
                  : translate(showMoreInfo ? 'notifications:panel.readLess' : 'notifications:panel.readMore')
              }
            </button>
          </>
        )
      }
      <div className={classes.ctaContainer}>
        {isValidEntityCTA && (
          <TertiaryButton className={classes.viewAssetCta} onClick={handleReviewClick}>
            {translate(`notifications:panel.${isDeviceAlert(notification) ? 'viewDevice' : 'viewAsset'}`)}
          </TertiaryButton>
        )}
        {!isHideViewEventCta && viewEventCta}
      </div>
    </div>
  );
};

export default NotificationCard;
